import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import DataTable from "react-data-table-component";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Badge,
  Input,
  Label,
  FormGroup,
  Button,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import BannerImageViewModal from "./helper/bannerImageViewModal";
import BannerRejectionModal from "./helper/bannerRejectionModal";
import ApproveConfirmationModal from "./helper/approveConfirmationModal";
import ModerationDetailsModal from "./helper/moderationDetailsModal";
import UserInfoModal from "../../components/Common/UserInfoModal";
import { getUserBannerList, approveBanner } from "./helper/userBannerApi";

const UserBannerPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [imageViewModal, setImageViewModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [rejectionModal, setRejectionModal] = useState(false);
  const [bannerToReject, setBannerToReject] = useState(null);
  const [approveModal, setApproveModal] = useState(false);
  const [bannerToApprove, setBannerToApprove] = useState(null);
  const [approveLoading, setApproveLoading] = useState(false);
  const [moderationModal, setModerationModal] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [userInfoModal, setUserInfoModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  // Filter states
  const [status, setStatus] = useState("");
  const [bannerId, setBannerId] = useState("");
  const [moderationResult, setModerationResult] = useState("");
  const [dateRange, setDateRange] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(50);
  const [totalRows, setTotalRows] = useState(0);

  // Refs for optimization
  const bannerIdTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);
  const isLoadingRef = useRef(false);
  const hasInitialFetchedRef = useRef(false);

  // Debounced banner ID
  const [debouncedBannerId, setDebouncedBannerId] = useState("");

  // Fetch list function
  const fetchList = useCallback(async () => {
    if (isLoadingRef.current) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    isLoadingRef.current = true;
    setLoading(true);

    try {
      const filters = {
        limit,
        page: currentPage,
        status: status || undefined,
        banner_id: debouncedBannerId || undefined,
        moderation_result: moderationResult || undefined,
        dateRange: dateRange && !startDate && !endDate ? dateRange : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      };

      const result = await getUserBannerList(filters);

      if (abortController.signal.aborted || !isMountedRef.current) {
        return;
      }

      setData(result.data);
      setTotalRows(result.pagination.totalRecords);
    } catch (error) {
      if (error.name !== "AbortError" && isMountedRef.current) {
        console.error("Error fetching user banner list:", error);
      }
    } finally {
      if (isMountedRef.current && !abortController.signal.aborted) {
        setLoading(false);
        isLoadingRef.current = false;
      }
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  }, [status, debouncedBannerId, moderationResult, dateRange, startDate, endDate, currentPage, limit]);

  // Handlers
  const handleViewImage = useCallback((banner) => {
    const imageUrl = banner.banner_image_url;
    setSelectedImage({
      imageUrl,
      bannerId: banner.id,
    });
    setImageViewModal(true);
  }, []);

  const handleViewModeration = useCallback((banner) => {
    setSelectedBanner(banner);
    setModerationModal(true);
  }, []);

  const handleViewUserInfo = useCallback((userId) => {
    if (!userId) return;
    setSelectedUserId(userId);
    setUserInfoModal(true);
  }, []);

  const handleApprove = useCallback((banner) => {
    setBannerToApprove(banner);
    setApproveModal(true);
  }, []);

  const handleApproveConfirm = useCallback(async () => {
    if (!bannerToApprove || !bannerToApprove.id) {
      alert("Banner ID is missing");
      return;
    }

    setApproveLoading(true);
    try {
      await approveBanner(bannerToApprove.id);
      // Refresh the list
      if (hasInitialFetchedRef.current) {
        fetchList();
      }
      setApproveModal(false);
      setBannerToApprove(null);
    } catch (error) {
      console.error("Error approving banner:", error);
      alert(error.response?.data?.message || error.message || "Failed to approve banner");
    } finally {
      setApproveLoading(false);
    }
  }, [bannerToApprove, fetchList]);

  const handleReject = useCallback((banner) => {
    setBannerToReject(banner);
    setRejectionModal(true);
  }, []);

  const handleRejectConfirm = useCallback(() => {
    // Refresh the list after rejection
    if (hasInitialFetchedRef.current) {
      fetchList();
    }
  }, [fetchList]);

  const columns = useMemo(
    () => [
      {
        name: <span className="font-weight-bold fs-13">Banner ID</span>,
        selector: (row) => row.id,
        sortable: true,
        cell: (row) => (
          <div 
            className="font-monospace small text-primary cursor-pointer"
            style={{ cursor: "pointer", textDecoration: "underline" }}
            onClick={() => handleViewModeration(row)}
            title="Click to view moderation details"
          >
            {row.id || "-"}
          </div>
        ),
      },
      {
        name: <span className="font-weight-bold fs-13">User ID</span>,
        selector: (row) => row.userId,
        sortable: true,
        cell: (row) => (
          <div 
            className="font-monospace small text-primary cursor-pointer"
            style={{ cursor: "pointer", textDecoration: "underline" }}
            onClick={() => handleViewUserInfo(row.userId)}
            title="Click to view user details"
          >
            {row.userId || "-"}
          </div>
        ),
      },
      {
        name: <span className="font-weight-bold fs-13">Status</span>,
        selector: (row) => row.status,
        sortable: true,
        cell: (row) => {
          const statusValue = row.status?.toLowerCase();
          switch (statusValue) {
            case "approved":
              return <Badge color="success" className="px-2 py-1">Approved</Badge>;
            case "rejected":
              return <Badge color="danger" className="px-2 py-1">Rejected</Badge>;
            case "pending":
              return <Badge color="warning" className="px-2 py-1">Pending</Badge>;
            default:
              return <Badge color="secondary" className="px-2 py-1">{row.status || "N/A"}</Badge>;
          }
        },
      },
      {
        name: <span className="font-weight-bold fs-13">Moderation Result</span>,
        selector: (row) => row.moderation?.moderation_result,
        sortable: true,
        cell: (row) => {
          const result = row.moderation?.moderation_result?.toLowerCase();
          if (result === "pass") {
            return <Badge color="success" className="px-2 py-1">Pass</Badge>;
          } else if (result === "fail") {
            return <Badge color="danger" className="px-2 py-1">Fail</Badge>;
          }
          return <Badge color="secondary" className="px-2 py-1">{row.moderation?.moderation_result || "N/A"}</Badge>;
        },
      },
      {
        name: <span className="font-weight-bold fs-13">Image</span>,
        cell: (row) => {
          const imageUrl = row.banner_image_url;
          if (!imageUrl) return "-";
          return (
            <Button
              color="info"
              size="sm"
              onClick={() => handleViewImage(row)}
              className="px-3"
            >
              <i className="mdi mdi-eye me-1"></i>View
            </Button>
          );
        },
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
      {
        name: <span className="font-weight-bold fs-13">Created At</span>,
        selector: (row) => row.created_at,
        sortable: true,
        cell: (row) => {
          const dateStr = row.created_at;
          if (!dateStr) return "-";
          const date = new Date(dateStr);
          return date.toLocaleString();
        },
      },
      {
        name: <span className="font-weight-bold fs-13">Actions</span>,
        cell: (row) => {
          const statusValue = row.status?.toLowerCase();
          const isPending = statusValue === "pending";
          
          return (
            <div className="d-flex gap-2">
              {isPending && (
                <>
                  <Button
                    color="success"
                    size="sm"
                    onClick={() => handleApprove(row)}
                    className="px-3"
                  >
                    <i className="mdi mdi-check me-1"></i>Approve
                  </Button>
                  <Button
                    color="danger"
                    size="sm"
                    onClick={() => handleReject(row)}
                    className="px-3"
                  >
                    <i className="mdi mdi-close me-1"></i>Reject
                  </Button>
                </>
              )}
              {!isPending && (
                <span className="text-muted small">No actions available</span>
              )}
            </div>
          );
        },
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
    ],
    [handleViewModeration, handleViewUserInfo, handleViewImage, handleApprove, handleReject]
  );

  // Initial fetch
  useEffect(() => {
    isMountedRef.current = true;
    if (!hasInitialFetchedRef.current) {
      fetchList();
      hasInitialFetchedRef.current = true;
    }

    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (bannerIdTimeoutRef.current) {
        clearTimeout(bannerIdTimeoutRef.current);
      }
    };
  }, []);

  // Debounce banner ID search
  useEffect(() => {
    if (bannerIdTimeoutRef.current) {
      clearTimeout(bannerIdTimeoutRef.current);
    }

    bannerIdTimeoutRef.current = setTimeout(() => {
      setDebouncedBannerId(bannerId);
    }, 1000);

    return () => {
      if (bannerIdTimeoutRef.current) {
        clearTimeout(bannerIdTimeoutRef.current);
      }
    };
  }, [bannerId]);

  // Fetch when debounced banner ID changes
  useEffect(() => {
    if (hasInitialFetchedRef.current && debouncedBannerId !== null) {
      setCurrentPage(1);
      fetchList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedBannerId]);

  // Fetch when page changes
  useEffect(() => {
    if (hasInitialFetchedRef.current) {
      fetchList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Fetch when other filters change
  useEffect(() => {
    if (hasInitialFetchedRef.current) {
      setCurrentPage(1);
      fetchList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, moderationResult, dateRange, startDate, endDate]);

  const handleFilterChange = (filterType, value) => {
    setCurrentPage(1);
    switch (filterType) {
      case "status":
        setStatus(value);
        break;
      case "bannerId":
        setBannerId(value);
        break;
      case "moderationResult":
        setModerationResult(value);
        break;
      case "dateRange":
        setDateRange(value);
        if (value !== "custom") {
          setStartDate("");
          setEndDate("");
        }
        break;
      case "startDate":
        setStartDate(value);
        if (value) setDateRange("custom");
        break;
      case "endDate":
        setEndDate(value);
        if (value) setDateRange("custom");
        break;
      default:
        break;
    }
  };

  const clearFilters = useCallback(() => {
    setStatus("");
    setBannerId("");
    setModerationResult("");
    setDateRange("");
    setStartDate("");
    setEndDate("");
    setDebouncedBannerId("");
    setCurrentPage(1);
  }, []);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="User Banner" breadcrumbItem="User Banner" />
          <Row>
            <Col xs={12}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">
                    <i className="mdi mdi-image-multiple me-2"></i>User Banner
                  </h4>
                </CardHeader>
                <CardBody>
                  {/* Filters */}
                  <Row className="mb-3">
                    <Col md={3}>
                      <FormGroup>
                        <Label>Status</Label>
                        <Input
                          type="select"
                          value={status}
                          onChange={(e) => handleFilterChange("status", e.target.value)}
                        >
                          <option value="">All</option>
                          <option value="pending">Pending</option>
                          <option value="approved">Approved</option>
                          <option value="rejected">Rejected</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Banner ID</Label>
                        <Input
                          type="text"
                          placeholder="Search by banner ID..."
                          value={bannerId}
                          onChange={(e) => handleFilterChange("bannerId", e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Moderation Result</Label>
                        <Input
                          type="select"
                          value={moderationResult}
                          onChange={(e) => handleFilterChange("moderationResult", e.target.value)}
                        >
                          <option value="">All</option>
                          <option value="pass">Pass</option>
                          <option value="fail">Fail</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Date Range</Label>
                        <Input
                          type="select"
                          value={dateRange}
                          onChange={(e) => handleFilterChange("dateRange", e.target.value)}
                        >
                          <option value="">None</option>
                          <option value="last_24_hours">Last 24 Hours</option>
                          <option value="last_7_days">Last 7 Days</option>
                          <option value="last_30_days">Last 30 Days</option>
                          <option value="custom">Custom Range</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    {(dateRange === "custom" || (startDate || endDate)) && (
                      <>
                        <Col md={3}>
                          <FormGroup>
                            <Label>Start Date</Label>
                            <Input
                              type="date"
                              value={startDate}
                              onChange={(e) => handleFilterChange("startDate", e.target.value)}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={3}>
                          <FormGroup>
                            <Label>End Date</Label>
                            <Input
                              type="date"
                              value={endDate}
                              onChange={(e) => handleFilterChange("endDate", e.target.value)}
                            />
                          </FormGroup>
                        </Col>
                      </>
                    )}
                    <Col md={12}>
                      <Button color="secondary" onClick={clearFilters} className="me-2">
                        <i className="mdi mdi-filter-remove me-1"></i>Clear Filters
                      </Button>
                    </Col>
                  </Row>

                  {/* Data Table */}
                  <DataTable
                    columns={columns}
                    data={data}
                    progressPending={loading}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationDefaultPage={currentPage}
                    onChangePage={(page) => setCurrentPage(page)}
                    paginationPerPage={limit}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    noDataComponent={
                      <div className="text-center py-4">
                        <i className="mdi mdi-image-off" style={{ fontSize: "3rem", color: "#6c757d" }}></i>
                        <p className="mt-3 text-muted">No banner images found</p>
                      </div>
                    }
                    highlightOnHover
                    pointerOnHover
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <BannerImageViewModal
        isOpen={imageViewModal}
        toggle={() => {
          setImageViewModal(false);
          setSelectedImage(null);
        }}
        imageUrl={selectedImage?.imageUrl}
        bannerId={selectedImage?.bannerId}
      />

      <BannerRejectionModal
        isOpen={rejectionModal}
        toggle={() => {
          setRejectionModal(false);
          setBannerToReject(null);
        }}
        banner={bannerToReject}
        onConfirm={handleRejectConfirm}
      />

      <ApproveConfirmationModal
        isOpen={approveModal}
        toggle={() => {
          setApproveModal(false);
          setBannerToApprove(null);
        }}
        banner={bannerToApprove}
        onConfirm={handleApproveConfirm}
        loading={approveLoading}
      />

      <ModerationDetailsModal
        isOpen={moderationModal}
        toggle={() => {
          setModerationModal(false);
          setSelectedBanner(null);
        }}
        banner={selectedBanner}
      />

      <UserInfoModal
        isOpen={userInfoModal}
        toggle={() => {
          setUserInfoModal(false);
          setSelectedUserId(null);
        }}
        userId={selectedUserId}
        onUpdate={() => {
          // Optionally refresh the list after user action
          if (hasInitialFetchedRef.current) {
            fetchList();
          }
        }}
      />
    </React.Fragment>
  );
};

export default UserBannerPage;

